/**
 * Performance Monitoring Utilities
 * Centralized performance measurement and optimization tools
 */

// Performance thresholds
export const PERFORMANCE_THRESHOLDS = {
  RENDER_TIME_WARNING: 16, // 1 frame at 60fps
  RENDER_TIME_CRITICAL: 32, // 2 frames at 60fps
  API_RESPONSE_WARNING: 1000, // 1 second
  API_RESPONSE_CRITICAL: 3000, // 3 seconds
  BUNDLE_SIZE_WARNING: 2 * 1024 * 1024, // 2MB
  MEMORY_USAGE_WARNING: 50 * 1024 * 1024, // 50MB
} as const;

// Performance metrics collector
class PerformanceMonitor {
  private metrics: Map<string, number[]> = new Map();
  private observations: PerformanceObserver[] = [];
  
  constructor() {
    this.initializeObservers();
  }

  private initializeObservers() {
    // Observe paint timing
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        const paintObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            this.recordMetric(`paint.${entry.name}`, entry.startTime);
          }
        });
        paintObserver.observe({ entryTypes: ['paint'] });
        this.observations.push(paintObserver);
      } catch (e) {
        console.warn('Paint observer not supported');
      }

      // Observe navigation timing
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('navigation.domContentLoaded', navEntry.domContentLoadedEventEnd - navEntry.navigationStart);
              this.recordMetric('navigation.loadComplete', navEntry.loadEventEnd - navEntry.navigationStart);
            }
          }
        });
        navigationObserver.observe({ entryTypes: ['navigation'] });
        this.observations.push(navigationObserver);
      } catch (e) {
        console.warn('Navigation observer not supported');
      }
    }
  }

  recordMetric(name: string, value: number) {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, []);
    }
    this.metrics.get(name)!.push(value);
    
    // Log warnings for slow operations
    this.checkThresholds(name, value);
  }

  private checkThresholds(name: string, value: number) {
    if (name.includes('render') && value > PERFORMANCE_THRESHOLDS.RENDER_TIME_WARNING) {
      console.warn(`🐌 [PERF] Slow render detected: ${name} took ${value.toFixed(2)}ms`);
    }
    
    if (name.includes('api') && value > PERFORMANCE_THRESHOLDS.API_RESPONSE_WARNING) {
      console.warn(`🐌 [PERF] Slow API response: ${name} took ${value.toFixed(2)}ms`);
    }
  }

  getAverageMetric(name: string): number {
    const values = this.metrics.get(name) || [];
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  }

  getAllMetrics(): Record<string, { average: number; count: number; latest: number }> {
    const result: Record<string, { average: number; count: number; latest: number }> = {};
    
    for (const [name, values] of this.metrics.entries()) {
      result[name] = {
        average: values.reduce((a, b) => a + b, 0) / values.length,
        count: values.length,
        latest: values[values.length - 1] || 0
      };
    }
    
    return result;
  }

  clearMetrics() {
    this.metrics.clear();
  }

  disconnect() {
    this.observations.forEach(observer => observer.disconnect());
    this.observations = [];
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Performance measurement utilities
export const measurePerformance = {
  // Measure function execution time
  measure: <T extends (...args: any[]) => any>(
    fn: T,
    name: string
  ): T => {
    return ((...args: any[]) => {
      const start = performance.now();
      const result = fn(...args);
      const duration = performance.now() - start;
      
      performanceMonitor.recordMetric(`function.${name}`, duration);
      
      if (duration > PERFORMANCE_THRESHOLDS.RENDER_TIME_WARNING) {
        console.warn(`🐌 [PERF] Slow function: ${name} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    }) as T;
  },

  // Measure async function execution time
  measureAsync: <T extends (...args: any[]) => Promise<any>>(
    fn: T,
    name: string
  ): T => {
    return (async (...args: any[]) => {
      const start = performance.now();
      const result = await fn(...args);
      const duration = performance.now() - start;
      
      performanceMonitor.recordMetric(`async.${name}`, duration);
      
      if (duration > PERFORMANCE_THRESHOLDS.API_RESPONSE_WARNING) {
        console.warn(`🐌 [PERF] Slow async operation: ${name} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    }) as T;
  },

  // Measure component render time
  measureRender: (componentName: string) => {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      performanceMonitor.recordMetric(`render.${componentName}`, duration);
      
      if (duration > PERFORMANCE_THRESHOLDS.RENDER_TIME_WARNING) {
        console.warn(`🐌 [PERF] Slow render: ${componentName} took ${duration.toFixed(2)}ms`);
      }
    };
  },

  // Create a performance timer
  startTimer: (name: string) => {
    const start = performance.now();
    return {
      end: () => {
        const duration = performance.now() - start;
        performanceMonitor.recordMetric(`timer.${name}`, duration);
        return duration;
      }
    };
  }
};

// Memory usage monitoring
export const memoryMonitor = {
  getCurrentUsage: (): number => {
    if ('memory' in performance) {
      return (performance as any).memory.usedJSHeapSize;
    }
    return 0;
  },

  logMemoryUsage: (context: string) => {
    const usage = memoryMonitor.getCurrentUsage();
    if (usage > 0) {
      const usageMB = (usage / 1024 / 1024).toFixed(2);
      console.log(`💾 [PERF] Memory usage in ${context}: ${usageMB}MB`);
      
      if (usage > PERFORMANCE_THRESHOLDS.MEMORY_USAGE_WARNING) {
        console.warn(`⚠️ [PERF] High memory usage detected: ${usageMB}MB`);
      }
    }
  },

  checkMemoryLeaks: () => {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      const usageRatio = memory.usedJSHeapSize / memory.totalJSHeapSize;
      
      if (usageRatio > 0.9) {
        console.warn('⚠️ [PERF] Potential memory leak detected - heap usage is very high');
      }
      
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        ratio: usageRatio
      };
    }
    return null;
  }
};

// Bundle size monitoring
export const bundleMonitor = {
  estimateSize: async (): Promise<number> => {
    try {
      // This is a rough estimation based on the main bundle
      const response = await fetch('/assets/index.js', { method: 'HEAD' });
      const contentLength = response.headers.get('content-length');
      return contentLength ? parseInt(contentLength, 10) : 0;
    } catch {
      return 0;
    }
  },

  checkBundleSize: async () => {
    const size = await bundleMonitor.estimateSize();
    if (size > PERFORMANCE_THRESHOLDS.BUNDLE_SIZE_WARNING) {
      console.warn(`📦 [PERF] Large bundle detected: ${(size / 1024 / 1024).toFixed(2)}MB`);
    }
    return size;
  }
};

// Performance debugging utilities
export const debugPerformance = {
  // Log all current metrics
  logAllMetrics: () => {
    const metrics = performanceMonitor.getAllMetrics();
    console.group('📊 [PERF] Performance Metrics');
    
    Object.entries(metrics).forEach(([name, data]) => {
      console.log(`${name}: avg=${data.average.toFixed(2)}ms, count=${data.count}, latest=${data.latest.toFixed(2)}ms`);
    });
    
    console.groupEnd();
  },

  // Start comprehensive monitoring
  startMonitoring: () => {
    const interval = setInterval(() => {
      memoryMonitor.logMemoryUsage('periodic-check');
      memoryMonitor.checkMemoryLeaks();
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  },

  // Generate performance report
  generateReport: () => {
    const metrics = performanceMonitor.getAllMetrics();
    const memory = memoryMonitor.checkMemoryLeaks();
    
    return {
      timestamp: new Date().toISOString(),
      metrics,
      memory,
      recommendations: generateRecommendations(metrics)
    };
  }
};

// Generate performance recommendations
function generateRecommendations(metrics: Record<string, any>): string[] {
  const recommendations: string[] = [];

  // Check for slow renders
  Object.entries(metrics).forEach(([name, data]) => {
    if (name.includes('render') && data.average > PERFORMANCE_THRESHOLDS.RENDER_TIME_WARNING) {
      recommendations.push(`Consider optimizing ${name} - average render time is ${data.average.toFixed(2)}ms`);
    }
    
    if (name.includes('api') && data.average > PERFORMANCE_THRESHOLDS.API_RESPONSE_WARNING) {
      recommendations.push(`Consider optimizing ${name} - average response time is ${data.average.toFixed(2)}ms`);
    }
  });

  return recommendations;
}

// React hook for component performance monitoring
export const usePerformanceMonitoring = (componentName: string) => {
  React.useEffect(() => {
    const endMeasure = measurePerformance.measureRender(componentName);
    return endMeasure;
  });

  React.useEffect(() => {
    memoryMonitor.logMemoryUsage(`${componentName}-mount`);
    
    return () => {
      memoryMonitor.logMemoryUsage(`${componentName}-unmount`);
    };
  }, [componentName]);
};

// Export for development debugging
if (typeof window !== 'undefined') {
  (window as any).__VRP_PERFORMANCE__ = {
    monitor: performanceMonitor,
    memory: memoryMonitor,
    bundle: bundleMonitor,
    debug: debugPerformance,
    measure: measurePerformance
  };
}